import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { toFixed } from "@shared/utils/numbers";
import { useSelector } from "react-redux";
import { ReactNode, useContext } from "react";
import {
  getAmountTypeSuffix,
  getBotAvailableAmountDisplay,
} from "@shared/features/bots/utils";
import useHedgingExposureBreakdown from "@shared/features/bots/BotForm/common/hooks/useHedgingExposureBreakdown";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { AmountType, BotType, LanguageNameSpaces } from "@shared/types";
import { isSymbol } from "@shared/types/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useBotFormSummary = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const {
    botAmountData,
    amountValue,
    amountType,
    botFormType,
    bulkActionBotsData,
    botType,
    initialBotAmountData,
    useSymbolFromAlert,
  } = useContext(BotFormContext)!;
  const hedgingBreakdown = useHedgingExposureBreakdown();
  const isBulkAction =
    botFormType === BotFormType.BULK_EDIT ||
    botFormType === BotFormType.BULK_CREATE;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isRiskPercentage = amountType === AmountType.RISK_PERCENTAGE;

  let botAmountPercent = 0;
  if (
    botAmountData &&
    amountType !== AmountType.RISK_PERCENTAGE &&
    !useSymbolFromAlert
  ) {
    const mainCoinAmount = toFixed(
      botAmountData.mainCoinAmount,
      botAmountData.mainCoinAmountPrecision
    );
    const availableMainCoinAmount = toFixed(
      botAmountData.mainCoinAvailableAmount,
      botAmountData.mainCoinAmountPrecision
    );
    botAmountPercent = toFixed(
      (mainCoinAmount / availableMainCoinAmount) * 100,
      2
    );
  }

  const showMaxBotAmountWarning =
    !isSignalsBot && !isBulkAction && botAmountPercent > MAX_PERCENT;
  let botAmountDisplay = "";
  let botAmountValueTooltipContent: SummaryTooltipContent | undefined;
  let botAmountValueHelperDescription: string | undefined;
  if (amountType && amountValue !== undefined && botAmountData) {
    if (isRiskPercentage) {
      botAmountDisplay = `${isSymbol(amountValue) ? "" : amountValue}% Risk`;
    } else if (useSymbolFromAlert) {
      botAmountDisplay = `${
        isSymbol(amountValue) ? "" : amountValue
      } ${getAmountTypeSuffix(amountType)}`;
    } else {
      const botAmount = botAmountData.convertMainCoinAmountToDisplayCurrency(
        botAmountData.mainCoinAmount,
        displayCurrency,
        true
      );
      const effectiveTotalBreakdown = botAmountData.effectiveTotalBreakdown;
      const displayPercent = effectiveTotalBreakdown
        ? toFixed(effectiveTotalBreakdown.percentage, 2)
        : botAmountPercent;
      const botPercentDisplay = useSymbolFromAlert
        ? ""
        : `${
            displayPercent > MAX_PERCENT ? `> ${MAX_PERCENT}` : displayPercent
          }%`;
      botAmountDisplay = `${botAmount || 0} ${displayCurrency}${
        botPercentDisplay ? ` (${botPercentDisplay})` : ""
      }`;
      if (effectiveTotalBreakdown) {
        const getDisplayAmount = (mainCoinAmount: number) => {
          const amount =
            botAmountData.convertMainCoinAmountToDisplayCurrency(
              mainCoinAmount,
              displayCurrency,
              true
            ) || 0;
          return `${amount} ${displayCurrency}`;
        };
        const availableAmount = getDisplayAmount(
          effectiveTotalBreakdown.availableAmount.mainCoin
        );
        const openTradesAmount = getDisplayAmount(
          effectiveTotalBreakdown.openTradesAmount.mainCoin
        );
        const totalAmount = getDisplayAmount(
          effectiveTotalBreakdown.totalAmount.mainCoin
        );
        const selectedAmount = getDisplayAmount(
          effectiveTotalBreakdown.selectedAmount.mainCoin
        );
        const percentage = toFixed(effectiveTotalBreakdown.percentage, 2);
        botAmountValueTooltipContent = {
          rows: [
            {
              label: t("labels.available_amount"),
              value: availableAmount,
            },
            {
              label: t("labels.open_trades_amount"),
              value: openTradesAmount,
            },
            {
              label: t("labels.effective_total"),
              value: totalAmount,
            },
          ],
          calculation: `${totalAmount} x ${percentage}% = ${selectedAmount}`,
        };
        botAmountValueHelperDescription = t(
          "helper_tooltips.form.bot_amount_value_effective_total.description"
        );
      }
    }
  }

  const getSummaryItems = () => {
    if (isBulkAction) {
      const numBots = bulkActionBotsData?.length || 0;
      const numValidBots =
        bulkActionBotsData?.filter((bot) => !!bot.validationData.isValid)
          .length || 0;
      return [
        {
          key: `${numBots} Bots were selected`,
          value: "",
        },
        {
          key: "Invalid Bots",
          value: numBots - numValidBots,
        },
        {
          key: "Valid Bots",
          value: numValidBots,
        },
      ];
    }
    if (isTradingViewSignalsBot) return [];
    const summaryItems: SummaryItem[] = [];
    if (!useSymbolFromAlert) {
      summaryItems.push({
        key: t("labels.available_balance"),
        value: getBotAvailableAmountDisplay(botAmountData, displayCurrency),
      });
    }
    summaryItems.push({
      key: t("labels.bot_amount_value"),
      value: botAmountDisplay,
      isWarning: showMaxBotAmountWarning,
      tooltipContent: botAmountValueTooltipContent,
      helperDescription: botAmountValueHelperDescription,
    });
    if (isGridBot) {
      summaryItems.push({
        key: t("labels.bot_initial_amount"),
        value: `${initialBotAmountData.initialUsdAmount} USD`,
      });
    }
    if (isHedgingBot) {
      if (!hedgingBreakdown) return summaryItems;
      const { longRatio, shortRatio, longAmountUsd, shortAmountUsd } =
        hedgingBreakdown;
      const longHedgingText = `${t("labels.long")} ${longRatio}% (${toFixed(
        longAmountUsd,
        2
      )} USD)`;
      const shortHedgingText = `${t("labels.short")} ${shortRatio}% (${toFixed(
        shortAmountUsd,
        2
      )} USD)`;
      summaryItems.push({
        key: t("labels.hedging"),
        value: (
          <>
            <div>{longHedgingText}</div>
            <div>{shortHedgingText}</div>
          </>
        ),
        isMultiline: true,
      });
    }
    return summaryItems;
  };

  const summaryItems: SummaryItem[] = getSummaryItems();

  const maxBotAmountWarningMessage = showMaxBotAmountWarning
    ? t("messages.warnings.amount_higher_than_available", {
        coin: botAmountData?.mainCoin || "",
      })
    : undefined;

  return {
    summaryItems,
    maxBotAmountWarningMessage,
    botAmountValueTitle: t("labels.bot_amount_value"),
  };
};

export default useBotFormSummary;

export interface SummaryItem {
  key: string;
  value: ReactNode;
  isWarning?: boolean;
  isMultiline?: boolean;
  tooltipContent?: SummaryTooltipContent;
  helperDescription?: string;
}

export interface SummaryTooltipContent {
  rows: SummaryTooltipRow[];
  calculation?: string;
}

export interface SummaryTooltipRow {
  label: string;
  value: string;
}
const MAX_PERCENT = 100;
